<script lang="ts">
  import { onMount, afterUpdate } from 'svelte';
  import { writable, type Writable } from 'svelte/store';
  import { tweened } from 'svelte/motion';
  import { cubicOut } from 'svelte/easing';
  import type { Store, Floor, ViewBox } from './mapUtils';
  import { DEFAULT_MAP_CONFIG } from './mapConstants';
  import { createEventDispatcher } from 'svelte';
  import { getStoresContext } from '~/stores';
  import type { StorePinData, StoreEventHandlers } from './store-layer/storeTypes';
  import { processAllStores } from './store-layer/storeProcessing';
  import { debugLog, debugWarn } from './store-layer/debugUtils';
  import { clearAllPins, createPinsForFloor } from './store-layer/pinUtils';
  import {
    clearAllLabels,
    createLabelsForFloor,
    createHoverLabel,
    clearHoverLabel
  } from './store-layer/labelUtils';

  // Import subcomponents
  import DebugPanel from './store-layer/DebugPanel.svelte';
  import ShapeMapper from './store-layer/ShapeMapper.svelte';
  import type { MappedShapeData } from './store-layer/ShapeMapper.svelte';
  import HighlightManager from './store-layer/HighlightManager.svelte';
  import StoreSidebar from './store-layer/StoreSidebar.svelte';
  
  // Import service location components
  import ServiceLocationRenderer from './service-locations/ServiceLocationRenderer.svelte';

  import { 
    processAllServiceLocations, 
    getServiceLocationsForFloor
  } from './service-locations/serviceLocationProcessing';
  import { 
    DEFAULT_SERVICE_LOCATION_CONFIG,
    type ServiceLocationData,
    type ServiceLocationState
  } from './service-locations/serviceLocationTypes';
  
  // Props
  export let svgContent: string = '';
  export let floors: Floor[] = [];
  export let currentFloorId: number | null = null;
  export let highlightedStoreId: string | null = null;
  export let autoZoomToStore: boolean = false;
  export let mapConfig: any = {};
  export let request_store_key;
  export let onStoreClick: (storeId: string) => void = (storeId: string) => {
    // Toggle selection logic
    if (internalHighlightedStoreId === storeId) {
      internalHighlightedStoreId = null;
    } else {
      internalHighlightedStoreId = storeId;
    }
    
    // Dispatch an event to notify parent components of the change
    dispatch('storeselect', { storeId: internalHighlightedStoreId });
  };
  export let onStoreHover: (storeId: string | null) => void = () => {};
  
  // Create a custom event dispatcher
  const dispatch = createEventDispatcher();
  
  // Internal state for highlighting, controlled by this component.
  let internalHighlightedStoreId: string | null = highlightedStoreId;
  let storeIdForHoverLabel: string | null = null;

  // Make config reactive and merge with defaults. This is the single source of truth.
  $: config = {
    ...DEFAULT_MAP_CONFIG,
    ...mapConfig,
    labelStyling: {
      ...DEFAULT_MAP_CONFIG.labelStyling,
      ...(mapConfig.labelStyling || {}),
    },
    dynamicLabeling: {
      ...DEFAULT_MAP_CONFIG.dynamicLabeling,
      ...(mapConfig.dynamicLabeling || {}),
    },
    shapeHighlighting: {
      ...DEFAULT_MAP_CONFIG.shapeHighlighting,
      ...(mapConfig.shapeHighlighting || {}),
    },
    storePins: {
      ...DEFAULT_MAP_CONFIG.storePins,
      ...(mapConfig.storePins || {}),
    },
    serviceLocations: (() => {
      const serviceConfig = mapConfig?.serviceLocations || {};
      return {
        ...DEFAULT_SERVICE_LOCATION_CONFIG,
        ...serviceConfig,
        pinStyling: {
          ...DEFAULT_SERVICE_LOCATION_CONFIG.pinStyling,
          ...(serviceConfig.pinStyling || {}),
        },
        labelStyling: {
          ...DEFAULT_SERVICE_LOCATION_CONFIG.labelStyling,
          ...(serviceConfig.labelStyling || {}),
        },
      };
    })()
  };
  
  // Internal state
  let mapContainer: HTMLDivElement;
  let isPanning: boolean = false;
  let startPoint: {x: number, y: number} | null = null;
  let isPinching: boolean = false;
  let lastTouchDistance: number | null = null;
  let initialTouchDistance: number | null = null;
  let initialViewBox: ViewBox | null = null;
  
  // Use a tweened store for smooth viewBox transitions
  let viewBox: Writable<ViewBox> = tweened({x: 0, y: 0, width: 1000, height: 1000}, {
    duration: 700,
    easing: cubicOut
  });
  
  let currentViewBox = {x: 0, y: 0, width: 1000, height: 1000};
  let currentFloor: Floor | null = null;
  let showFloors = false;
  let pendingZoomStoreId: string | null = null;
  
  // Zoom/pan state
  let originalViewBox = { x: 0, y: 0, width: 1000, height: 1000 };
  let mapCenter = { x: 500, y: 500 };
  let atMinZoom = false;
  let atMaxZoom = false;
  let atPanBoundary = false;
  let sidebarContainer: HTMLDivElement;
  let mapControlsContainer: HTMLDivElement;
  
  // This is the reference to the currently rendered SVG element. It's the key to syncing the layers.
  let renderedSvgElement: SVGSVGElement | null = null;

  // Store Layer State
  const { stores: storesContext, service_locations: serviceLocationsContext } = getStoresContext(request_store_key);
  let initialized = false;
  let cachedPinsByFloor = new Map<number, StorePinData[]>();
  let activeFloorPins: StorePinData[] = [];
  let eventHandlers = new Map<string, StoreEventHandlers>();
  let shapeMapperComponent: ShapeMapper;
  let highlightManagerComponent: HighlightManager;
  let initialZoomDone = false;
  let mappedShapesForCurrentFloor: MappedShapeData[] = [];
  let isSidebarVisible = true;

  // Service location state - simplified for static display
  let serviceLocationState = {
    visibleServiceLocations: [] as ServiceLocationData[]
  };
  
  // Service location variables - simplified
  let serviceLocationInitialized = false;
  let cachedServiceLocationsByFloor = new Map<number, ServiceLocationData[]>();
  let activeFloorServiceLocations: ServiceLocationData[] = [];
  let serviceLocationRendererComponent: ServiceLocationRenderer | undefined;

  // Function to find a store by its ID across all floors
  function findStoreById(storeId: string): (StorePinData & { floorId: number }) | null {
    if (!cachedPinsByFloor) return null;
    for (const [floorId, pins] of cachedPinsByFloor.entries()) {
      const foundPin = pins.find(p => p.id === storeId);
      if (foundPin) {
        return { ...foundPin, floorId };
      }
    }
    return null;
  }
  
  // The definitive function to select a store and trigger a zoom.
  function selectAndZoomToStore(store: StorePinData & { floorId: number }) {
    if (!store) return;
    
    // Set the pending ID and highlight the store immediately.
    pendingZoomStoreId = store.id;
    internalHighlightedStoreId = store.id;
    dispatch('storeselect', { store }); // To update sidebar UI

    // If the store is on a different floor, changing the floor ID
    // will trigger the reactive renderer.
    if (store.floorId !== currentFloorId) {
      currentFloorId = store.floorId;
      } else {
      // If on the same floor, the renderer won't run, so we must
      // manually trigger the shape search.
      if (shapeMapperComponent) {
        shapeMapperComponent.findShapesForPins();
      }
    }
  }

  // Handle selection from the sidebar
  function handleStoreSelection(event: CustomEvent<{ store: StorePinData & { floorId: number } }>) {
    const { store } = event.detail;
    if (store) {
      selectAndZoomToStore(store);
      // On mobile, collapse the sidebar to show the map after selection
      if (window.innerWidth <= 768) {
        isSidebarVisible = false;
      }
    } else {
      internalHighlightedStoreId = null;
    }
  }

  // Reactive declarations
  $: zoomLevel = Math.round((originalViewBox.width / currentViewBox.width) * 100);
  $: currentFloor = floors.find(f => f.id === currentFloorId) || null;

  // Combine viewport stats into a single object for the debug panel
  $: viewportStats = {
      x: currentViewBox.x,
      y: currentViewBox.y,
      width: currentViewBox.width,
      height: currentViewBox.height,
      zoomLevel,
      atMinZoom,
      atMaxZoom,
    atPanBoundary,
  };

  // Watch for changes to trigger auto-zoom ONCE on initial load
  $: if (autoZoomToStore && highlightedStoreId && initialized && !initialZoomDone) {
    const storeToSelect = findStoreById(highlightedStoreId);
    if (storeToSelect) {
      selectAndZoomToStore(storeToSelect);
      initialZoomDone = true;
    } else {
      initialZoomDone = true; // Don't try again
    }
  }

  // Process raw store data once it's loaded
  $: if ($storesContext && $storesContext.length > 0 && !initialized) {
    const result = processAllStores([...$storesContext], floors);
    cachedPinsByFloor = result.cachedPinsByFloor;
    initialized = result.initialized;
  }

  // Process service locations data once it's loaded
  $: if ($serviceLocationsContext && $serviceLocationsContext.length > 0 && !serviceLocationInitialized && config?.serviceLocations?.enabled) {
    // Get service locations from the stores context
    const serviceLocations = $serviceLocationsContext || [];
    debugLog('Service Locations Data: ' + JSON.stringify(serviceLocations), 'serviceLocations');
    debugLog(`Loading ${serviceLocations.length} service locations from context`, 'serviceLocations');
    
    if (serviceLocations.length > 0) {
      // Log raw service location data for debugging
      debugLog(`Raw service location data sample:`, 'serviceLocations');
      serviceLocations.slice(0, 3).forEach((service, index) => {
        debugLog(`  [${index}] ID: ${service.id}, Type: ${service.serviceType}, Floor: ${service.floorIndex}, X: ${service.x}, Y: ${service.y}`, 'serviceLocations');
      });
      
      const result = processAllServiceLocations([...serviceLocations], floors);
      cachedServiceLocationsByFloor = result.cachedServiceLocationsByFloor;
      serviceLocationInitialized = result.initialized;
      
      debugLog(`Service locations processed. Initialized: ${result.initialized}`, 'serviceLocations');
      debugLog(`Cached service locations for ${cachedServiceLocationsByFloor.size} floors`, 'serviceLocations');
      

    }
  }

  // Main renderer: runs whenever the floor changes OR the SVG element is swapped.
  $: if (renderedSvgElement && currentFloorId !== null && initialized) {
    renderPinsAndLabels(currentFloorId);
  }

  // Service location renderer: runs when floor changes and service locations are enabled
  $: if (renderedSvgElement && currentFloorId !== null && serviceLocationInitialized && config?.serviceLocations?.enabled) {
    renderServiceLocationsForFloor(currentFloorId);
  }

  // This new reactive block ensures that the permanent labels are always in sync
  // with the currently selected store.
  $: if (renderedSvgElement && mappedShapesForCurrentFloor.length > 0 && config.showLabels) {
    let shapesToLabel;

    // If dynamic labeling is on, filter the shapes to be labeled.
    if (config.dynamicLabeling.enabled) {
      shapesToLabel = mappedShapesForCurrentFloor.filter(shape => {
        // Always show the active store's label, regardless of zoom.
        if (shape.id === internalHighlightedStoreId) return true;
        
        switch (shape.areaCategory) {
          case 'Big':
            return zoomLevel >= 100; // Standard zoom level
          case 'Medium':
            return zoomLevel >= config.dynamicLabeling.mediumLabelZoomThreshold;
          case 'Small':
            return zoomLevel >= config.dynamicLabeling.smallLabelZoomThreshold;
          default:
            return false;
        }
      });
    } else {
      // Dynamic labeling is off, so only show the label for the active store, if any.
      shapesToLabel = internalHighlightedStoreId 
        ? mappedShapesForCurrentFloor.filter(shape => shape.id === internalHighlightedStoreId)
        : [];
    }
    
    createLabelsForFloor(
      renderedSvgElement,
      shapesToLabel,
      config.showLabels,
      config.showStorePins,
      config.labelStyling
    );
  } else if (renderedSvgElement) {
    clearAllLabels(renderedSvgElement);
  }

  function renderPinsAndLabels(floorId: number) {
    if (!renderedSvgElement) return;
    debugLog(`Rendering pins and labels for floor ${floorId}`, 'floorChanges');
    
    // Get pins for this floor from cache
    activeFloorPins = cachedPinsByFloor.get(floorId) || [];
    if (!renderedSvgElement) return;
    debugLog(`Found ${activeFloorPins.length} pins for floor ${floorId}`, 'floorChanges');
    
    // Clear previous renders
    clearAllPins(renderedSvgElement, eventHandlers);
    clearAllLabels(renderedSvgElement);
    clearHoverLabel(renderedSvgElement); // Also clear any lingering hover labels
    mappedShapesForCurrentFloor = []; // Reset the shapes for the new floor
    
    // If we have pins for this floor, find their shapes to render labels
    if (activeFloorPins.length > 0) {
      if (shapeMapperComponent) {
        // ShapeMapper is a child component, so we need to wait for it to be ready.
        // `afterUpdate` ensures the component exists before we call its methods.
        afterUpdate(() => {
          if (shapeMapperComponent) {
            shapeMapperComponent.findShapesForPins();
          }
        });
      }
      
      // Conditionally create the visual pins based on the config
      if (config.showStorePins) {
        createPinsForFloor(
          renderedSvgElement, 
          activeFloorPins, 
          onStoreClick, 
          onStoreHover, 
          eventHandlers, 
          internalHighlightedStoreId,
          config.enableStoreClick,
          config.enableStoreHover,
          config.storePins
        );
      }
      
      debugLog(`Rendered labels. Waiting for shapes to render pins if enabled.`, 'floorChanges');
    }
  }

  function renderServiceLocationsForFloor(floorId: number) {
    if (!renderedSvgElement || !config?.serviceLocations?.enabled) return;
    debugLog(`Rendering service locations for floor ${floorId}`, 'serviceLocations');
    
    // Get service locations for this floor from cache - NO FILTERING, show all
    const floorServiceLocations = getServiceLocationsForFloor(cachedServiceLocationsByFloor, floorId);
    debugLog(`Found ${floorServiceLocations.length} service locations for floor ${floorId}`, 'serviceLocations');
    
    // No filtering - use all service locations
    activeFloorServiceLocations = floorServiceLocations;
    
    // Update visible service locations in state
    serviceLocationState.visibleServiceLocations = activeFloorServiceLocations;
    
    // Force render through the service location renderer component
    if (serviceLocationRendererComponent && serviceLocationRendererComponent.forceRender) {
      serviceLocationRendererComponent.forceRender();
    }
  }



  function handleShapesFound(event: CustomEvent<{ shapes: MappedShapeData[] }>) {
    const shapes = event.detail.shapes;
    if (renderedSvgElement && shapes.length > 0) {
      debugLog(`Received ${shapes.length} shapes from ShapeMapper. Caching data.`, 'floorChanges');
      
      // Update the cached data with the new shape information
      if (currentFloorId !== null) {
        const floorPins = cachedPinsByFloor.get(currentFloorId) || [];
        shapes.forEach(shape => {
            const pinIndex = floorPins.findIndex(p => p.id === shape.id);
            if (pinIndex !== -1) {
                // Merge the new shape data into the cached pin data
                const existingPin = floorPins[pinIndex];
                floorPins[pinIndex] = { ...existingPin, ...shape };
            }
        });
        // This reassignment is crucial for Svelte's reactivity
        cachedPinsByFloor = new Map(cachedPinsByFloor.set(currentFloorId, floorPins));
      }

      // Store the mapped shapes in our component's state.
      // The new reactive block will now handle rendering the labels.
      mappedShapesForCurrentFloor = shapes;

      // If a zoom is pending for a store on this floor, execute it now
      if (pendingZoomStoreId) {
        const storeToZoom = shapes.find(s => s.id === pendingZoomStoreId);
        if (storeToZoom) {
          zoomToStore(storeToZoom);
          // Now that we have zoomed, explicitly highlight the shape
          if (highlightManagerComponent && storeToZoom.id) {
            highlightManagerComponent.highlightShapeById(storeToZoom.id);
          }
          pendingZoomStoreId = null; // Clear the pending zoom
        }
      }
    }
  }

  $: {
    if (svgContent && mapContainer) {
      const svgContentDiv = mapContainer.querySelector('.svg-content');
      if (svgContentDiv) {
          svgContentDiv.innerHTML = svgContent;
          // After rendering, get the SVG element reference.
          // The reactive block above will then trigger the pin/label rendering.
          renderedSvgElement = svgContentDiv.querySelector('svg');
          if (renderedSvgElement) {
            initializeViewBox();
            // Add background click handler for deselection
            renderedSvgElement.addEventListener('click', handleBackgroundClick);
          }
      }
    }
  }

  function handleBackgroundClick(e: MouseEvent) {
    // If store clicks are disabled, do nothing
    if (!config.enableStoreClick) return;
    
    const target = e.target as Element;
    // Do not deselect if the click is on a store pin or shape
    if (target && (target.closest('.store-pin') || target.closest('.mapped-store-shape'))) {
      return;
    }
    
    // If a store is selected, deselect it
    if (internalHighlightedStoreId !== null) {
      internalHighlightedStoreId = null;
      // Notify parent components
      dispatch('storeselect', { storeId: null });
    }
  }
  
  viewBox.subscribe(value => {
    currentViewBox = value;
    if (renderedSvgElement) {
      renderedSvgElement.setAttribute('viewBox', `${value.x} ${value.y} ${value.width} ${value.height}`);
    }
    dispatch('viewportUpdate', { viewBox: value });
  });
  
  // Floor navigation
  function nextFloor() {
    if (floors.length <= 1) return;
    const currentIndex = floors.findIndex(f => f.id === currentFloorId);
    currentFloorId = floors[(currentIndex + 1) % floors.length].id;
  }
  
  function prevFloor() {
    if (floors.length <= 1) return;
    const currentIndex = floors.findIndex(f => f.id === currentFloorId);
    currentFloorId = floors[(currentIndex - 1 + floors.length) % floors.length].id;
  }
  
  function selectFloor(floorId: number) {
    currentFloorId = floorId;
  }
  
  function initializeViewBox() {
    if (!renderedSvgElement) return;

    const svgViewBox = renderedSvgElement.getAttribute('viewBox');
      if (svgViewBox) {
        const [x, y, width, height] = svgViewBox.split(' ').map(Number);
      originalViewBox = { x, y, width, height };
          } else {
        const bbox = renderedSvgElement.getBBox();
        originalViewBox = { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
          }
          
      mapCenter = {
        x: originalViewBox.x + originalViewBox.width / 2,
        y: originalViewBox.y + originalViewBox.height / 2
      };
      
    viewBox.set({ ...originalViewBox });
  }
  
  // Touch event handlers
  function handleTouchStart(event: TouchEvent) {
    // ALWAYS handle pinch gestures first - they override everything
    if (event.touches.length === 2 && config.allowZoom) {
      isPinching = true;
      isPanning = false; // Cancel any panning
      startPoint = null; // Clear pan start point
      
      // Lock in the initial state for continuous pinch-to-zoom
      initialTouchDistance = getTouchDistance(event.touches);
      lastTouchDistance = initialTouchDistance;
      initialViewBox = { ...currentViewBox }; // Snapshot the starting viewBox
      
      event.preventDefault();
      event.stopPropagation(); // Stop store shape handlers from interfering
      return;
    }
    
    // Skip UI elements for single touches
    if (
      (sidebarContainer && sidebarContainer.contains(event.target as Node)) ||
      (mapControlsContainer && mapControlsContainer.contains(event.target as Node))
    ) {
      return;
    }
    
    // Handle single touch - could be tap or pan, decide later based on movement
    if (event.touches.length === 1 && config.allowPan && !isPinching) {
      isPanning = true;
      startPoint = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      // Don't prevent default immediately - wait to see if this becomes a pan gesture
      // Don't check for store shapes yet - let movement determine the behavior
    }
  }

    function handleTouchMove(event: TouchEvent) {
    // Handle pinch gestures (don't need startPoint for pinching)
    if (isPinching && event.touches.length >= 2 && initialTouchDistance && initialViewBox) {
      const currentTouchDistance = getTouchDistance(event.touches);
      
      // Continuous pinch-to-zoom: calculate total zoom from the initial distance
      const totalZoomRatio = currentTouchDistance / initialTouchDistance;
      
      // Symmetric aggressive scaling for full range in single gesture
      // Use the same exponent for both directions to ensure equal zoom speed
      const exponent = 2.5;
      let cumulativeZoomFactor;
      if (totalZoomRatio > 1) {
        // Zooming in: exponential scaling
        cumulativeZoomFactor = Math.pow(totalZoomRatio, exponent);
      } else {
        // Zooming out: symmetric inverse scaling
        cumulativeZoomFactor = 1 / Math.pow(1 / totalZoomRatio, exponent);
      }

      const rect = mapContainer.getBoundingClientRect();
      const touchCenterX = (event.touches[0].clientX + event.touches[1].clientX) / 2 - rect.left;
      const touchCenterY = (event.touches[0].clientY + event.touches[1].clientY) / 2 - rect.top;

      // Calculate zoom based on the INITIAL viewBox, not current
      const svgX = initialViewBox.x + (touchCenterX / rect.width) * initialViewBox.width;
      const svgY = initialViewBox.y + (touchCenterY / rect.height) * initialViewBox.height;

      const newWidth = initialViewBox.width / cumulativeZoomFactor;
      const newHeight = initialViewBox.height / cumulativeZoomFactor;
      const newX = svgX - (touchCenterX / rect.width) * newWidth;
      const newY = svgY - (touchCenterY / rect.height) * newHeight;

      (viewBox as any).set(applyConstraints({ x: newX, y: newY, width: newWidth, height: newHeight }));
      event.preventDefault();
      return;
    }

    // Handle panning (needs startPoint)
    if (!startPoint) return;
    
    if (isPanning && event.touches.length === 1) {
      // Calculate movement distance to determine if this is a pan gesture
      const moveX = Math.abs(event.touches[0].clientX - startPoint.x);
      const moveY = Math.abs(event.touches[0].clientY - startPoint.y);
      const totalMovement = Math.sqrt(moveX * moveX + moveY * moveY);
      
      // Only start panning if the user has moved more than 5 pixels
      if (totalMovement > 5) {
        // Once we detect significant movement, panning takes priority over everything
        // This allows panning to work even if it started on a store shape
        const dx = (event.touches[0].clientX - startPoint.x) * (currentViewBox.width / mapContainer.clientWidth) * config.panSensitivity;
        const dy = (event.touches[0].clientY - startPoint.y) * (currentViewBox.height / mapContainer.clientHeight) * config.panSensitivity;
        const newViewBox = { ...currentViewBox, x: currentViewBox.x - dx, y: currentViewBox.y - dy };
        (viewBox as any).set(applyConstraints(newViewBox));
        startPoint = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        event.preventDefault(); // Prevent store interactions once we're panning
        event.stopPropagation(); // Stop store shape handlers from interfering
      }
    }
  }

    function handleTouchEnd(event: TouchEvent) {
    // Only prevent default if we were actually panning or pinching
    // This allows taps on store shapes to work properly
    if ((isPanning || isPinching) && startPoint) {
      // Check if this was a tap (minimal movement) vs a pan/pinch gesture
      if (isPanning && event.changedTouches.length === 1) {
        const moveX = Math.abs(event.changedTouches[0].clientX - startPoint.x);
        const moveY = Math.abs(event.changedTouches[0].clientY - startPoint.y);
        const totalMovement = Math.sqrt(moveX * moveX + moveY * moveY);
        
        // If movement was minimal, this was a tap - don't prevent default
        // This allows taps on store shapes to work even if panning was initialized
        if (totalMovement <= 5) {
          // This was a tap, let it through to store handlers
          // Don't prevent default so store interactions can still work
        } else {
          // This was actual panning, prevent default
    event.preventDefault();
  }
      } else if (isPinching) {
    event.preventDefault();
      }
  }
    
    isPanning = false;
    isPinching = false;
    startPoint = null;
    lastTouchDistance = null;
    initialTouchDistance = null;
    initialViewBox = null;
  }

  function getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  // Mouse wheel zoom functionality
  function handleWheel(event: WheelEvent) {
    if (sidebarContainer && sidebarContainer.contains(event.target as Node)) {
      return; // Do not zoom if scroll is inside the sidebar
    }
    if (!config.allowZoom || !config.wheelZoom) return;
    event.preventDefault();
    
    const scale = config.zoomSensitivity / 100;
    const zoomFactor = event.deltaY < 0 ? (1 + scale) : (1 - scale);
    
    const rect = mapContainer.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    const svgX = currentViewBox.x + (mouseX / rect.width) * currentViewBox.width;
    const svgY = currentViewBox.y + (mouseY / rect.height) * currentViewBox.height;
    const newWidth = currentViewBox.width * zoomFactor;
    const newHeight = currentViewBox.height * zoomFactor;
    const newX = svgX - (mouseX / rect.width) * newWidth;
    const newY = svgY - (mouseY / rect.height) * newHeight;
    (viewBox as any).set(applyConstraints({ x: newX, y: newY, width: newWidth, height: newHeight }));
  }
  
  // Pan and Zoom functions (zoomIn, zoomOut, handleMouseDown, etc.) remain the same
  function getFloorName(floor: Floor | null): string {
    if (!floor) return 'No floor selected';
    if (typeof floor.name === 'string') return floor.name;
    if (Array.isArray(floor.name)) {
      const localizedName = floor.name[0];
      return localizedName?.text || `Floor ${floor.id}`;
    }
    return `Floor ${floor.id}`;
  }
  
  function applyConstraints(newViewBox: ViewBox): ViewBox {
    const zoomLevel = originalViewBox.width / newViewBox.width;
    atMinZoom = zoomLevel < config.minZoom;
    atMaxZoom = zoomLevel >= config.maxZoom;
    
    let constrainedWidth = newViewBox.width;
    let constrainedHeight = newViewBox.height;
    
    if (atMinZoom) {
      constrainedWidth = originalViewBox.width / config.minZoom;
      constrainedHeight = originalViewBox.height / config.minZoom;
    } else if (atMaxZoom) {
      constrainedWidth = originalViewBox.width / config.maxZoom;
      constrainedHeight = originalViewBox.height / config.maxZoom;
    }
    
    const viewBoxCenterX = newViewBox.x + newViewBox.width / 2;
    const viewBoxCenterY = newViewBox.y + newViewBox.height / 2;
    let constrainedCenterX = viewBoxCenterX;
    let constrainedCenterY = viewBoxCenterY;
    
    const distanceX = Math.abs(viewBoxCenterX - mapCenter.x);
    if (distanceX > config.panLimit) {
      constrainedCenterX = mapCenter.x + (Math.sign(viewBoxCenterX - mapCenter.x) * config.panLimit);
    }
    
    // Calculate a proportional pan limit for the Y-axis
    const aspectRatio = originalViewBox.height / originalViewBox.width;
    const panLimitY = config.panLimit * aspectRatio;

    const distanceY = Math.abs(viewBoxCenterY - mapCenter.y);
    if (distanceY > panLimitY) {
      constrainedCenterY = mapCenter.y + (Math.sign(viewBoxCenterY - mapCenter.y) * panLimitY);
    }

    atPanBoundary = (distanceX > config.panLimit || distanceY > panLimitY);

    return {
      x: constrainedCenterX - constrainedWidth / 2,
      y: constrainedCenterY - constrainedHeight / 2,
      width: constrainedWidth,
      height: constrainedHeight
    };
  }

  // Zoom functions
  function zoom(factor: number) {
    const newWidth = currentViewBox.width * factor;
    const newHeight = currentViewBox.height * factor;
    
    const centerX = currentViewBox.x + currentViewBox.width / 2;
    const centerY = currentViewBox.y + currentViewBox.height / 2;

    const newX = centerX - newWidth / 2;
    const newY = centerY - newHeight / 2;

    viewBox.set(applyConstraints({ x: newX, y: newY, width: newWidth, height: newHeight }));
  }

  function zoomIn() {
    zoom(0.8);
  }

  function zoomOut() {
    zoom(1.2);
  }

  function resetZoom() {
    viewBox.set({ ...originalViewBox });
  }

  function zoomToStore(store: MappedShapeData) {
    if (!store.bbox) {
      debugWarn('[SvgMapViewer] zoomToStore: Store has no bbox, cannot zoom.', store);
        return;
    }

    // Use the configured zoom factor for a consistent zoom level
    const targetWidth = originalViewBox.width / config.storeZoomFactor;
    const targetHeight = originalViewBox.height / config.storeZoomFactor;

    // Center the view on the store's bounding box
    const storeCenterX = store.bbox.x + store.bbox.width / 2;
    const storeCenterY = store.bbox.y + store.bbox.height / 2;
    
    const targetX = storeCenterX - targetWidth / 2;
    const targetY = storeCenterY - targetHeight / 2;

    const newViewBox = { x: targetX, y: targetY, width: targetWidth, height: targetHeight };

    // Set the viewbox using applyConstraints to enforce limits and update zoom state flags
    (viewBox as any).set(applyConstraints(newViewBox));
  }

  function internalOnStoreHover(storeId: string | null) {
    // Handle our internal logic for showing hover labels
    // On mobile, don't show hover labels (they don't make sense with touch)
    const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;
    if (config.labelStyling.showLabelOnHover && !isMobile) {
      storeIdForHoverLabel = storeId;
    }
    // Call the parent's onStoreHover function if it was provided
    if (onStoreHover) {
      onStoreHover(storeId);
    }
  }

  // Reactive block to handle showing/hiding the hover label
  $: if (renderedSvgElement) {
    // A hover label should appear only if:
    // 1. The feature is enabled in the config.
    // 2. A store is currently being hovered.
    // 3. The hovered store is NOT the same as the one that is currently selected (active).
    const shouldShowHoverLabel =
      config.labelStyling.showLabelOnHover &&
      storeIdForHoverLabel &&
      storeIdForHoverLabel !== internalHighlightedStoreId;

    if (shouldShowHoverLabel && currentFloorId !== null) {
      const floorPins = cachedPinsByFloor.get(currentFloorId);
      const shapeData = floorPins?.find(p => p.id === storeIdForHoverLabel);

      // Ensure we have the full shape data with coordinates before creating a label
      if (shapeData && 'labelPosition' in shapeData) {
        createHoverLabel(
          renderedSvgElement,
          shapeData as MappedShapeData,
          config.showStorePins,
          config.labelStyling
        );
      } else {
        // If shape data isn't ready for some reason, ensure the label is cleared
        clearHoverLabel(renderedSvgElement);
      }
    } else {
      // In all other cases, ensure the hover label is cleared
      clearHoverLabel(renderedSvgElement);
    }
  }

  // Pan functionality
  function handleMouseDown(event: MouseEvent) {
    if (sidebarContainer && sidebarContainer.contains(event.target as Node)) {
      return; // Do not pan if click is inside the sidebar
    }
    if (!config.allowPan || event.button !== 0) return;
    mapContainer.classList.add('panning');
    isPanning = true;
    startPoint = { x: event.clientX, y: event.clientY };
    event.preventDefault();
  }
  
  function handleMouseMove(event: MouseEvent) {
    if (!config.allowPan || !isPanning || !startPoint) return;
    const dx = (event.clientX - startPoint.x) * (currentViewBox.width / mapContainer.clientWidth) * config.panSensitivity;
    const dy = (event.clientY - startPoint.y) * (currentViewBox.height / mapContainer.clientHeight) * config.panSensitivity;
    const newViewBox = { ...currentViewBox, x: currentViewBox.x - dx, y: currentViewBox.y - dy };
    (viewBox as any).set(applyConstraints(newViewBox));
    startPoint = { x: event.clientX, y: event.clientY };
    event.preventDefault();
  }
  
  function handleMouseUp() {
    mapContainer?.classList.remove('panning');
    isPanning = false;
    startPoint = null;
  }

  onMount(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      // If we are on a store page (highlightedStoreId is passed),
      // start with the sidebar collapsed. Otherwise, show it.
      isSidebarVisible = !highlightedStoreId;
    }
  });
</script>

<div 
  class="svg-map-container"
  bind:this={mapContainer}
  on:mousedown={handleMouseDown}
  on:mousemove={handleMouseMove}
  on:mouseup={handleMouseUp}
  on:mouseleave={handleMouseUp}
  on:wheel={handleWheel}
  on:touchstart={handleTouchStart}
  on:touchmove={handleTouchMove}
  on:touchend={handleTouchEnd}
>
  <div class="svg-content">
      <!-- svelte-ignore security-warn -->
      {@html svgContent}
    </div>
    
  {#if config.showStoreSidebar}
    <div bind:this={sidebarContainer}>
      <StoreSidebar
        isVisible={isSidebarVisible}
        allStores={cachedPinsByFloor}
        {floors}
        {currentFloorId}
        highlightedStoreId={internalHighlightedStoreId}
        on:storeselect={handleStoreSelection}
        on:toggle={() => (isSidebarVisible = !isSidebarVisible)}
      />
    </div>
  {/if}

  {#if renderedSvgElement}
    <DebugPanel 
      mapSvgElement={renderedSvgElement}
      {currentFloorId}
      {cachedPinsByFloor}
      {activeFloorPins}
      {highlightedStoreId}
      {viewportStats}
    />
    
    <ShapeMapper
      bind:this={shapeMapperComponent}
      svgElement={renderedSvgElement}
      pins={activeFloorPins}
      {onStoreClick}
      onStoreHover={internalOnStoreHover}
      {eventHandlers}
      highlightedStoreId={internalHighlightedStoreId}
      enableStoreClick={config.enableStoreClick}
      enableStoreHover={config.enableStoreHover}
      shapeHighlighting={config.shapeHighlighting}
      dynamicLabelingConfig={config.dynamicLabeling}
      on:shapesfound={handleShapesFound}
    />
    
    <HighlightManager
      bind:this={highlightManagerComponent}
      svgElement={renderedSvgElement}
      highlightedStoreId={internalHighlightedStoreId}
      {currentFloorId}
      {cachedPinsByFloor}
      shapeHighlighting={config.shapeHighlighting}
      storePins={config.storePins}
    />
    
    <!-- Service Location Components -->
    {#if config.serviceLocations.enabled}
      <ServiceLocationRenderer
        bind:this={serviceLocationRendererComponent}
        svgElement={renderedSvgElement}
        serviceLocations={activeFloorServiceLocations}
        config={config.serviceLocations}

      />
    {/if}
  {/if}
    
    <!-- Controls -->
    <div class="map-controls" bind:this={mapControlsContainer}>
      {#if config.allowZoom && config.showZoomControls}
        <div class="zoom-controls">
          <button on:click={zoomIn} title="Zoom in" disabled={atMaxZoom}>+</button>
          <button on:click={zoomOut} title="Zoom out" disabled={atMinZoom}>-</button>
          <button on:click={resetZoom} title="Reset view">⌾</button>
        </div>
      {/if}
      {#if config.showFloorControls}
        <div class="floor-controls">
          <button class="floor-btn" on:click={nextFloor} disabled={floors.length <= 1}>▲</button>
          <div class="floor-indicator" on:mouseenter={() => showFloors = true} on:mouseleave={() => showFloors = false}>
            <div class="current-floor">{getFloorName(currentFloor)}</div>
            {#if showFloors && floors.length > 1}
            <div class="floor-dropdown">
                {#each floors.slice().reverse() as floor (floor.id)}
                <button 
                  class="floor-option" 
                    class:active={floor.id === currentFloorId}
                    on:click={() => { selectFloor(floor.id); showFloors = false; }}
                  >
                    {getFloorName(floor)}
                </button>
              {/each}
            </div>
            {/if}
          </div>
          <button class="floor-btn" on:click={prevFloor} disabled={floors.length <= 1}>▼</button>
        </div>
      {/if}
    </div>

    <!-- Floor Indicator -->
    {#if config.showFloorIndicator}
      <div class="floor-indicator-display floor-indicator-{config.floorIndicatorPosition}">
        {config.ssrCurrentFloorName || getFloorName(currentFloor)}
      </div>
    {/if}
</div>

<style>
  .svg-map-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: #f5f5f5;
    user-select: none;
    touch-action: none; /* Prevent browser handling of touch gestures */
    transition: background-color 0.3s ease;
  }
  
  /* Visual feedback when limits are reached */
  .svg-map-container.at-min-zoom {
    background-color: rgba(255, 230, 230, 0.3);
  }
  
  .svg-map-container.at-max-zoom {
    background-color: rgba(230, 255, 230, 0.3);
  }
  
  .svg-map-container.at-boundary {
    background-color: rgba(230, 230, 255, 0.3);
  }
  
  /* Apply different cursor when panning */
  .svg-map-container.panning {
    cursor: grabbing;
  }
  
  /* Make sure the SVG content works correctly with the store layer */
  .svg-content {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    opacity: 1;
    transition: opacity 0.2s ease-out;
  }
  
  /* Floor transition animations */
  .svg-content.fade-out {
    opacity: 0;
    transform: scale(0.98);
  }
  
  .svg-content.fade-in {
    opacity: 1;
    transform: scale(1);
  }
  
  .svg-content :global(svg) {
    width: 100%;
    height: 100%;
    object-fit: contain;
    will-change: transform, opacity; /* Optimize for animations */
  }
  
  .store-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 5; /* Make sure it's above the SVG content */
  }
  
  /* Controls */
  .map-controls {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    gap: 10px;
    z-index: 100;
  }
  
  .zoom-controls {
    display: flex;
    justify-content: center; /* Center buttons */
    gap: 5px;
    background: white;
    border-radius: 4px;
    padding: 5px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    width: fit-content;
    margin: 0 auto;
  }
  
  .zoom-controls button {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ddd;
    background: white;
    border-radius: 4px;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.2s ease;
    -webkit-appearance: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    color: var(--body-color);
  }
  
  .zoom-controls button:hover {
    background-color: #f0f0f0;
  }
  
  .zoom-controls button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f5f5f5;
  }
  
  .floor-controls {
    display: flex;
    align-items: center;
    background: white;
    border-radius: 4px;
    padding: 5px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    position: relative;
    z-index: 200; /* Ensure it's above other elements */
    transition: background-color 0.3s ease;
  }
  
  .floor-controls.floor-changed {
    background-color: #f0f7ff;
    box-shadow: 0 0 10px rgba(66, 133, 244, 0.5);
    animation: pulse 0.5s;
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  .floor-btn {
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
    position: relative;
    z-index: 201; /* Ensure it's above other elements */
    -webkit-appearance: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    color: var(--body-color);
  }
  
  .floor-btn:hover {
    background-color: #f0f0f0;
  }
  
  .floor-btn:active {
    background-color: #e0e0e0;
  }
  
  .floor-indicator {
    position: relative;
    margin: 0 10px;
    min-width: 175px;
    z-index: 202; /* Ensure it's above other elements */
  }
  
  .current-floor {
    display: block;
    padding: 6px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    text-align: center;
    cursor: pointer;
    user-select: none;
  }
  
  .floor-dropdown {
    position: absolute;
    bottom: 100%;
    left: 0;
    width: 100%;
    background: white;
    border-radius: 4px;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.3s;
    opacity: 0;
    display: flex;
    flex-direction: column-reverse;
    z-index: 300; /* Higher z-index for dropdown */
  }
  
  /* Show dropdown on hover */
  .floor-indicator:hover .floor-dropdown {
    max-height: 300px;
    opacity: 1;
  }
  
  .floor-option {
    padding: 8px 12px;
    text-align: center;
    background: white;
    border: none;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    transition: background-color 0.2s;
    position: relative;
    user-select: none;
    z-index: 301; /* Even higher z-index */
    -webkit-appearance: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    color: var(--body-color);
  }
  
  .floor-option:hover {
    background-color: #f5f5f5;
  }
  
  .floor-option:active {
    background-color: #e0e0e0;
  }
  
  .floor-option.active {
    background-color: #e8f0fe;
    font-weight: bold;
  }
  
  .store-on-floor-indicator {
    position: absolute;
    right: 8px;
    color: #4285f4;
  }

  /* Floor Indicator Display */
  .floor-indicator-display {
    position: absolute;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 8px 12px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    font-weight: bold;
    z-index: 50;
    pointer-events: none;
    user-select: none;
    color: var(--body-color);
    font-size: 14px;
    min-width: 60px;
    text-align: center;
  }

  /* Position variants */
  .floor-indicator-top-left {
    top: 20px;
    left: 20px;
  }

  .floor-indicator-top-right {
    top: 20px;
    right: 20px;
  }

  .floor-indicator-bottom-left {
    bottom: 20px;
    left: 20px;
  }

  .floor-indicator-bottom-right {
    bottom: 20px;
    right: 20px;
  }
</style> 